import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { companiesApi } from '@/lib/companies';
import { Modal, InlineAlert } from '@/components/app/Modal';

interface WorkspaceLite { id: string; name: string; companyId: string }

/**
 * Workspace switcher dropdown shown in the AppShell topbar next to the
 * current workspace name. Lists every workspace the user can access
 * across all of their companies, plus a "+ New workspace" item that
 * opens an inline modal calling `POST /api/workspaces`.
 *
 * Active workspace is highlighted; clicking another navigates to its
 * `/app/workspace/<id>` overview.
 */
export function WorkspaceSwitcher({
  currentWorkspaceId,
  currentWorkspaceName,
}: {
  currentWorkspaceId: string;
  currentWorkspaceName: string | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.list(),
  });
  const companyIds = companies.map((c) => c.id);

  // Every workspace across ALL of the user's companies — not just companies[0].
  const { data: workspaces = [] } = useQuery({
    queryKey: ['workspaces', 'all', companyIds.join(',')],
    queryFn: async () => {
      const lists = await Promise.all(
        companyIds.map((id) =>
          api.get<WorkspaceLite[]>('/api/workspaces', { params: { companyId: id } }).then((r) => r.data),
        ),
      );
      return lists.flat();
    },
    enabled: companyIds.length > 0,
  });

  // New workspaces are created in the company of the workspace you're viewing
  // (falls back to the first company), so multi-company users land in the right place.
  const currentCompanyId =
    workspaces.find((w) => w.id === currentWorkspaceId)?.companyId ?? companies[0]?.id;
  const companyName = (id: string) => companies.find((c) => c.id === id)?.name ?? '';
  const multiCompany = companies.length > 1;

  const create = useMutation({
    mutationFn: async () => {
      if (!currentCompanyId) throw new Error('No company');
      const r = await api.post<WorkspaceLite>('/api/workspaces', { name: newName.trim() }, {
        params: { companyId: currentCompanyId },
      });
      return r.data;
    },
    onSuccess: (ws) => {
      setCreating(false);
      setOpen(false);
      setNewName('');
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      navigate(`/app/workspace/${ws.id}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.switch_workspace', { defaultValue: 'Switch workspace' }) as string}
        className="flex items-center gap-1.5 text-left max-w-[200px] hover:text-ink-700 transition"
      >
        <span className="truncate text-[13.5px] font-medium text-ink-900">
          {currentWorkspaceName || '…'}
        </span>
        <svg viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0 text-ink-500" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path strokeLinecap="round" d="M3 5l3 3 3-3" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.12 }}
              className="absolute left-0 mt-2 w-72 z-40 rounded-xl border border-ink-100 bg-white shadow-card overflow-hidden"
              role="menu"
            >
              <div className="px-4 py-2 border-b border-ink-100 text-[10.5px] uppercase tracking-[0.14em] text-ink-500 font-semibold">
                {t('nav.workspaces', { defaultValue: 'Workspaces' })}
              </div>
              <ul className="max-h-72 overflow-y-auto py-1">
                {[...workspaces]
                  .sort((a, b) => a.companyId.localeCompare(b.companyId) || a.name.localeCompare(b.name))
                  .map((w, i, arr) => {
                    const showHeader = multiCompany && (i === 0 || arr[i - 1].companyId !== w.companyId);
                    return (
                      <li key={w.id}>
                        {showHeader && (
                          <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                            {companyName(w.companyId)}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => { setOpen(false); navigate(`/app/workspace/${w.id}`); }}
                          className={`w-full text-left px-4 py-2 text-[13px] flex items-center justify-between gap-2 hover:bg-ink-50 ${
                            w.id === currentWorkspaceId ? 'bg-accent-50/60 text-ink-900' : 'text-ink-700'
                          }`}
                        >
                          <span className="truncate">{w.name}</span>
                          {w.id === currentWorkspaceId && (
                            <span className="text-[10px] uppercase tracking-[0.14em] text-accent-600 font-semibold">
                              {t('nav.current', { defaultValue: 'current' })}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
              </ul>
              <div className="border-t border-ink-100 py-1">
                <button
                  type="button"
                  onClick={() => { setOpen(false); setCreating(true); }}
                  className="w-full text-left px-4 py-2 text-[13px] text-accent-700 hover:bg-accent-50/40 font-medium"
                >
                  {t('nav.new_workspace', { defaultValue: '+ New workspace' })}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Modal
        open={creating}
        onOpenChange={(next) => { setCreating(next); if (!next) { setNewName(''); create.reset(); setError(null); } }}
        title={t('nav.new_workspace_title', { defaultValue: 'Create a new workspace' }) as string}
        description={t('nav.new_workspace_body', {
          defaultValue: 'Each workspace gets its own Qdrant collection + ES index. Switch between them from the topbar.',
        }) as string}
      >
        {error && <InlineAlert tone="error" onDismiss={() => setError(null)}>{error}</InlineAlert>}
        <label className="block mt-1">
          <span className="block text-[11px] uppercase tracking-[0.12em] text-ink-500 mb-1 font-semibold">
            {t('nav.new_workspace_name', { defaultValue: 'Workspace name' })}
          </span>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Sales · APAC"
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            autoFocus
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => setCreating(false)} className="btn-ghost px-3.5 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button
            onClick={() => { setError(null); create.mutate(); }}
            disabled={create.isPending || !newName.trim() || !currentCompanyId}
            className="btn-press px-3.5 py-2 text-sm"
          >
            {create.isPending ? t('nav.creating', { defaultValue: 'Creating…' }) : t('nav.create', { defaultValue: 'Create' })}
          </button>
        </div>
      </Modal>
    </div>
  );
}
